import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Table, Alert, Spinner, Button, Stack, Row, Col, Badge, Form, Modal, ListGroup, Card } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../../../firebase';
import { collection, getDocs, query, orderBy, doc, deleteDoc, where } from 'firebase/firestore';
import { useAuth } from '../../../components/context/AuthContext';
import { useNotification } from '../../../components/ui/Notification';
import BreadcrumbNavigation from '../../../components/ui/BreadcrumbNavigation'; // Import Breadcrumb
import Pagination from 'react-bootstrap/Pagination';
import { createLogger } from '../../../utils/logger';
import { effectiveDisplayPrice, totalDealsCountForInventory } from '../../../utils/inventoryPricing';

const logger = createLogger('InventoryPage');

const ITEMS_PER_PAGE = 10;
const LOW_STOCK_THRESHOLD = 10;

const InventoryPage = () => {
  const { userRole, employeeSupermarketId, currentUser } = useAuth();
  const { showNotification } = useNotification();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Discount deals modal state
  const [showDealsModal, setShowDealsModal] = useState(false);
  const [selectedItemDeals, setSelectedItemDeals] = useState([]);
  const [selectedItemName, setSelectedItemName] = useState('');
  
  const navigate = useNavigate();

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    userRole === 'Admin' ? { path: "/admin", label: "Admin Panel" } : { path: "/employee/dashboard", label: "Employee Hub" },
    { label: "Inventory Management" }
  ];

  // Handle viewing discount deals
  const flattenDealsForItem = (item) => {
    const out = [];
    const tdd = item.type_discount_deals;
    if (tdd && typeof tdd === 'object') {
      Object.entries(tdd).forEach(([typeId, deals]) => {
        if (!Array.isArray(deals)) return;
        deals.forEach((d) => out.push({ ...d, _dealTypeId: typeId }));
      });
    }
    if (out.length === 0 && item.discount_deals?.length) {
      item.discount_deals.forEach((d) => out.push({ ...d }));
    }
    return out;
  };

  const handleViewDeals = (item) => {
    setSelectedItemDeals(flattenDealsForItem(item));
    setSelectedItemName(item.product_name || 'Unknown Product');
    setShowDealsModal(true);
  };

  const handleCloseDealsModal = () => {
    setShowDealsModal(false);
    setSelectedItemDeals([]);
    setSelectedItemName('');
  };

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let inventoryQuery;
      if (userRole === 'Admin') {
        inventoryQuery = query(collection(db, 'Inventory'), orderBy('created_at', 'desc'));
      } else if (userRole === 'Employee' && employeeSupermarketId) {
        inventoryQuery = query(
          collection(db, 'Inventory'), 
          where('supermarket_id', '==', employeeSupermarketId),
          orderBy('created_at', 'desc')
        );
      } else if (userRole === 'Employee' && !employeeSupermarketId) {
        setInventory([]); setLoading(false); return;
      } else {
        setError("You are not authorized to view inventory."); setInventory([]); setLoading(false); return;
      }
      const inventorySnapshot = await getDocs(inventoryQuery);
      const inventoryList = inventorySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setInventory(inventoryList);
    } catch (err) {
      setError(`Failed to fetch inventory. ${  err.message}`);
      showNotification({
        type: 'error',
        title: 'Error',
        message: `Failed to fetch inventory. ${err.message}`
      });
    } finally {
      setLoading(false);
    }
  }, [userRole, employeeSupermarketId, showNotification]);

  useEffect(() => {
    if(currentUser && userRole){ // Ensure userRole is available before fetching
        fetchInventory();
    }
  }, [currentUser, userRole, fetchInventory]); // Added userRole to dependencies

  const handleGoBack = () => navigate(-1);
  const handleEdit = (itemId) => navigate(`/inventory/edit/${itemId}`);

  const handleDelete = async (itemId, itemSupermarketId) => {
    // Validate input parameters
    if (!itemId || typeof itemId !== 'string' || itemId.trim() === '') {
      showNotification({
        type: 'error',
        title: 'Invalid Input',
        message: 'Invalid inventory item ID.'
      });
      return;
    }

    if (userRole === 'Employee' && itemSupermarketId !== employeeSupermarketId) {
        showNotification({
          type: 'warning',
          title: 'Access Denied',
          message: 'You can only delete inventory items for your assigned supermarket.'
        });
        return;
    }
    
    // Enhanced confirmation dialog
    const itemToDelete = inventory.find(item => item.id === itemId);
    const itemName = itemToDelete ? (itemToDelete.product_name || 'Unknown Product') : 'this item';
    const supermarketName = itemToDelete ? (itemToDelete.supermarket_name || 'Unknown hardware store') : 'Unknown hardware store';
    
    const confirmMessage = `Are you sure you want to delete "${itemName}" from ${supermarketName}?\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      // First check if the document exists before trying to delete it
      const docRef = doc(db, 'Inventory', itemId);
      
      // Attempt to delete the document
      await deleteDoc(docRef);
      
      // Update local state only after successful deletion
      setInventory((prevInventory) => prevInventory.filter((item) => item.id !== itemId));
      
      // Show success notification
      showNotification({
        type: 'success',
        title: 'Success',
        message: `Successfully deleted "${itemName}" from inventory.`
      });
      
    } catch (err) {
      logger.error('Delete error:', err);
      
      // Provide more specific error messages based on the error type
      let errorMessage = `Failed to delete item ${itemId}. Error: ${err.message}`;
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied. You don\'t have access to delete this item.';
      } else if (err.code === 'not-found') {
        errorMessage = 'Item not found. It may have already been deleted.';
      } else if (err.code === 'unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again.';
      }
      
      showNotification({
        type: 'error',
        title: 'Delete Failed',
        message: errorMessage
      });
    }
  };

  const canAddInventory = userRole === 'Admin' || (userRole === 'Employee' && employeeSupermarketId);

  // Filter inventory based on search term
  const filteredInventory = useMemo(() => {
    if (!searchTerm.trim()) return inventory;
    
    const searchLower = searchTerm.toLowerCase();
    return inventory.filter(item => 
      (item.product_name && item.product_name.toLowerCase().includes(searchLower)) ||
      (item.supermarket_name && item.supermarket_name.toLowerCase().includes(searchLower)) ||
      (item.created_by_email && item.created_by_email.toLowerCase().includes(searchLower)) ||
      (item.sale_price && item.sale_price.toString().includes(searchLower)) ||
      (item.type_prices &&
        Object.values(item.type_prices).some((v) => v != null && String(v).includes(searchLower))) ||
      (item.quantity && item.quantity.toString().includes(searchLower))
    );
  }, [inventory, searchTerm]);

  // Dashboard summary stats (from full inventory, not search-filtered)
  const summaryStats = useMemo(() => {
    let totalQuantity = 0;
    let lowStock = 0;
    let outOfStock = 0;
    let itemsWithDeals = 0;
    let totalValue = 0;
    for (const item of inventory) {
      const q = typeof item.quantity === 'number' ? item.quantity : 0;
      const price = effectiveDisplayPrice(item) ?? (typeof item.sale_price === 'number' ? item.sale_price : 0);
      totalQuantity += q;
      if (q === 0) outOfStock++;
      else if (q > 0 && q <= LOW_STOCK_THRESHOLD) lowStock++;
      if (totalDealsCountForInventory(item) > 0) itemsWithDeals++;
      totalValue += q * price;
    }
    return {
      totalItems: inventory.length,
      totalQuantity,
      lowStock,
      outOfStock,
      itemsWithDeals,
      totalValue
    };
  }, [inventory]);

  // Pagination logic
  const totalPages = Math.ceil(filteredInventory.length / ITEMS_PER_PAGE);
  const paginatedInventory = filteredInventory.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (page) => setCurrentPage(page);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    // This component is embedded, so no <Layout> here.
    // The parent (InventoryPublicPageWrapper) provides the Layout.
    <Container className="pt-3">
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={handleGoBack} className="mb-2">
                    &larr; Go Back
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>
        
        <Row className="mb-3 align-items-center">
            <Col>
                <h2>Inventory Management</h2>
            </Col>
            {canAddInventory && (
                <Col xs="auto" className="text-end">
                    <Link to="/inventory/create" className="btn btn-primary">Add Inventory Item</Link>
                </Col>
            )}
        </Row>

        {/* Inventory Dashboard */}
        {!loading && !(userRole === 'Employee' && !employeeSupermarketId) && (
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-chart-line me-2" aria-hidden="true" />
                Dashboard Summary
              </h5>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-primary">
                    <Card.Body className="text-center">
                      <div className="text-primary mb-2">
                        <i className="fas fa-boxes fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">{summaryStats.totalItems}</h3>
                      <p className="text-muted mb-0 small">Total Items</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-info">
                    <Card.Body className="text-center">
                      <div className="text-info mb-2">
                        <i className="fas fa-cubes fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">{summaryStats.totalQuantity}</h3>
                      <p className="text-muted mb-0 small">Total Quantity</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-warning">
                    <Card.Body className="text-center">
                      <div className="text-warning mb-2">
                        <i className="fas fa-exclamation-triangle fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">{summaryStats.lowStock}</h3>
                      <p className="text-muted mb-0 small">Low Stock (≤{LOW_STOCK_THRESHOLD})</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-danger">
                    <Card.Body className="text-center">
                      <div className="text-danger mb-2">
                        <i className="fas fa-times-circle fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">{summaryStats.outOfStock}</h3>
                      <p className="text-muted mb-0 small">Out of Stock</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-success">
                    <Card.Body className="text-center">
                      <div className="text-success mb-2">
                        <i className="fas fa-tags fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">{summaryStats.itemsWithDeals}</h3>
                      <p className="text-muted mb-0 small">Items with Deals</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Card className="h-100 border-secondary">
                    <Card.Body className="text-center">
                      <div className="text-secondary mb-2">
                        <i className="fas fa-dollar-sign fa-2x" aria-hidden="true" />
                      </div>
                      <h3 className="mb-1">${summaryStats.totalValue.toFixed(2)}</h3>
                      <p className="text-muted mb-0 small">Total Value</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Search Bar */}
        <Row className="mb-4">
            <Col md={6} lg={4}>
                <div className="position-relative">
                    <Form.Control
                        type="text"
                        placeholder="Search inventory..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-100"
                        style={{ fontSize: '16px' }} // Prevents zoom on iOS
                    />
                    {searchTerm && (
                        <Button
                            variant="outline-secondary"
                            onClick={() => setSearchTerm('')}
                            style={{ 
                                fontSize: '16px', 
                                minWidth: '44px',
                                position: 'absolute',
                                right: '0',
                                top: '0',
                                height: '100%',
                                borderTopLeftRadius: '0',
                                borderBottomLeftRadius: '0',
                                zIndex: 10
                            }}
                        >
                            ×
                        </Button>
                    )}
                </div>
                <small className="text-muted">
                    Search by product name, supermarket, price, quantity, or added by
                </small>
            </Col>
        </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {userRole === 'Employee' && !employeeSupermarketId && !loading && (
          <Alert variant="warning">You are not assigned to a supermarket. Inventory management is unavailable.</Alert>
      )}

      {loading ? (
        <div className="text-center"><Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner></div>
      ) : (
        <>
          <Table bordered hover responsive striped>
            <thead>
              <tr>
                <th>#</th><th>Hardware store</th><th>Product</th><th>Sale Price</th><th>Quantity</th><th>Deals</th><th>Added By</th><th>Added At</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInventory.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center">
                    {searchTerm.trim() 
                      ? `No inventory items found matching "${searchTerm}"`
                      : `No inventory items found ${userRole === 'Employee' && employeeSupermarketId ? `for your assigned supermarket` : userRole === 'Employee' ? '(No supermarket assigned)' : '.' }`
                    }
                  </td>
                </tr>
              ) : (
                paginatedInventory.map((item, idx) => {
                  const canModify = userRole === 'Admin' || (userRole === 'Employee' && item.supermarket_id === employeeSupermarketId);
                  const dealCount = totalDealsCountForInventory(item);
                  return (
                    <tr key={item.id}>
                      <td>{(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}</td>
                      <td>{item.supermarket_name || item.supermarket_id}</td>
                      <td>{item.product_name || item.product_id}</td>
                      <td>${(effectiveDisplayPrice(item) ?? item.sale_price ?? 0).toFixed(2)}</td>
                      <td>{item.quantity}</td>
                      <td>
                        {dealCount > 0 ? (
                          <Badge bg="success">
                            {dealCount} deal{dealCount !== 1 ? 's' : ''}
                          </Badge>
                        ) : (
                          <span className="text-muted">None</span>
                        )}
                      </td>
                      <td>{item.created_by_email || item.created_by}</td>
                      <td>{item.created_at?.toDate().toLocaleString()}</td>
                      <td>
                        {canModify ? (
                          <Stack direction="horizontal" gap={2}>
                            <Button variant="warning" size="sm" onClick={() => handleEdit(item.id)}>Edit</Button>
                            <Button 
                              variant="danger" 
                              size="sm" 
                              onClick={() => handleDelete(item.id, item.supermarket_id)}
                              disabled={!item.id || item.id.trim() === ''}
                            >
                              Delete
                            </Button>
                          </Stack>
                        ) : (
                          <Badge bg="secondary">View Only</Badge>
                        )}
                        {dealCount > 0 && (
                          <Button 
                            variant="info" 
                            size="sm" 
                            onClick={() => handleViewDeals(item)}
                            className="mt-1"
                          >
                            View Deals ({dealCount})
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mb-4">
              <Pagination>
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                {[...Array(totalPages)].map((_, idx) => (
                  <Pagination.Item
                    key={idx + 1}
                    active={currentPage === idx + 1}
                    onClick={() => handlePageChange(idx + 1)}
                  >
                    {idx + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
          
          {/* Search Results Summary */}
          {searchTerm.trim() && (
            <div className="text-center text-muted mb-3">
              <small>
                Showing {filteredInventory.length} of {inventory.length} inventory items
                {filteredInventory.length !== inventory.length && ` matching "${searchTerm}"`}
              </small>
            </div>
          )}
        </>
      )}

             {/* Discount Deals Modal */}
       <Modal show={showDealsModal} onHide={handleCloseDealsModal} size="lg">
         <Modal.Header closeButton>
           <Modal.Title>Discount Deals for {selectedItemName}</Modal.Title>
         </Modal.Header>
         <Modal.Body>
           {selectedItemDeals.length === 0 ? (
             <p>No discount deals currently available for this item.</p>
           ) : (
             <ListGroup>
                               {selectedItemDeals.map((deal, index) => (
                  <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                    <div>
                      {deal._dealTypeId && (
                        <small className="text-muted d-block mb-1">Type ID: {deal._dealTypeId}</small>
                      )}
                      <strong>{deal.quantity} items</strong> for <strong>${deal.price.toFixed(2)}</strong>
                      <br />
                      <small className="text-success">
                        Per item: ${(deal.price / deal.quantity).toFixed(2)}
                      </small>
                    </div>
                    <Badge bg="success">Deal</Badge>
                  </ListGroup.Item>
                ))}
             </ListGroup>
           )}
         </Modal.Body>
         <Modal.Footer>
           <Button variant="secondary" onClick={handleCloseDealsModal}>Close</Button>
         </Modal.Footer>
       </Modal>
    </Container>
  );
};

export default InventoryPage;
